import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { IMAGE_FILETYPE, MARK_DISABLED_COLOR, PERMISSION_CODE, TenantConfig } from '../../app.constants';
import { CommonSaveConfirmDialogComponent } from '../../common-dialog/common-save-confirm-dialog.component';
import { InspDataMultiUpdate } from '../../inspection/inspection-data-manage.service';
import { Inspection } from '../../_objects/inspection';
import { MarkItem } from '../../_objects/mark';
import { TitleItem } from '../../_objects/title';
import { LangUpdateService } from '../../_services/lang-update.service';
import { TenantConfigService } from '../../_services/http-api/tenant-config.service';
import { SearchSelectOption } from "../../_shared/component/search-combo-box/search-select-option";
import { InspectionImageListItem } from '../../_objects/inspection-image';

export type MultiImagesEditDialogData = {
  selectedImages: InspectionImageListItem[],
  inspections: Inspection[],
  marks: MarkItem[],
  titles: TitleItem[],
  tags: string[]
}

@Component({
    selector: 'app-multiple-image-edit-dialog',
    templateUrl: './multiple-images-edit-dialog.component.html',
    styleUrls: ['./multiple-images-edit-dialog.component.scss'],
    standalone: false
})
export class MultipleImagesEditDialogComponent implements OnInit, OnDestroy {
  readonly commentMaxLength = 1000;
  public inspections: Inspection[] = [];
  public marks: MarkItem[];
  public titles: TitleItem[];
  public titleOptions : SearchSelectOption[];
  public tags: string[];
  public markControl = new FormControl<number>(null);
  public titleControl = new FormControl<string>('');
  public tagsControl = new FormControl<string[]>([]);
  public commentControl = new FormControl<string>('');
  public isCommentClear = false;
  public isTagsClear = false;
  public isNoChangeComment = true;
  public selectedImages: InspectionImageListItem[];
  public isProcessing = false;
  public isShowTags = false;

  markLabel = TenantConfig.DefaultConfigurationValues.LabelOfMark;
  titleLabel = TenantConfig.DefaultConfigurationValues.LabelOfTitle;
  tagsLabel = TenantConfig.DefaultConfigurationValues.LabelOfTags;

  private onDestroy$ = new Subject();

  @Output() updateEvent  = new EventEmitter<InspDataMultiUpdate>();
  @Output() deleteEvent = new EventEmitter();

  get isEdit(): boolean {
    return this.inspections &&
      this.inspections.some(insp => insp.permission && (insp.permission & PERMISSION_CODE.WRITE) !== 0);
  }

  constructor(
    private dialog: MatDialog,
    private tenantConfigService: TenantConfigService,
    public dialogRef: MatDialogRef<CommonSaveConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MultiImagesEditDialogData,
    private translate: TranslateService,
    private langService: LangUpdateService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.selectedImages = data.selectedImages;
    this.inspections = data.inspections;
    this.marks = data.marks;
    this.titles = data.titles;
    this.tags = data.tags;
    this.initTitleOptions(data.titles);
  }

  ngOnInit(): void {
    this.setConfig();
    this.valueInitialization();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  // Retrieve TenantConfig
  setConfig() {
    const configs = this.tenantConfigService.tenantConfig;
    if (configs) {
      this.markLabel = configs.find(_ => _.key === TenantConfig.LabelOfMark.KEY).value as string;
      this.titleLabel = configs.find(_ => _.key === TenantConfig.LabelOfTitle.KEY).value as string;
      this.tagsLabel = configs.find(_ => _.key === TenantConfig.LabelOfTags.KEY).value as string;
      this.isShowTags = configs.find(_ => _.key === TenantConfig.ShowTagsOnImages.KEY).value as boolean;
    }
  }

  valueInitialization() {
    this.markControl.setValue(null);
    this.titleControl.setValue(null);
    this.tagsControl.setValue([]);
    this.commentControl.setValue('');
    this.commentControl.enable();
    this.isCommentClear = false;
    this.isTagsClear = false;
  }

  getMarkById(identifier: number): MarkItem {
    let mark: MarkItem = new MarkItem();
    if (this.marks) {
      this.marks.forEach(item => {
        if (item.identifier === identifier) {
          mark = item;
        }
      });
      return mark;
    }
  }

  getFormattedTitleIndex(index: number) {
    return index < 9 ? `0${index + 1}` : `${index + 1}`;
  }

  initTitleOptions(titles: TitleItem[]) {
    var defaultTitles :SearchSelectOption[]  = [
      new SearchSelectOption(null, this.translate.instant('multipleImagesEditDialog.noChange')),
      new SearchSelectOption("", `00 - ${this.translate.instant('multipleImagesEditDialog.none')}`)
    ];
    this.titleOptions = defaultTitles.concat(titles
      .map(((title, index) =>
        new SearchSelectOption(title.identifier, `${this.getFormattedTitleIndex(index)} - ${title.identifier}`))));
  }
  onTitleChange(selectedValue: string) {
    this.titleControl.setValue(selectedValue);
  }

  // Determine if comment modification is possible
  changeCommentDisable() {
    if (this.isCommentClear) {
      this.commentControl.disable();
      this.commentControl.setValue('');
    } else {
      this.commentControl.enable();
    }
  }
  // Determine if tag modification is possible
  changeTagsDisable() {
    if (this.isTagsClear) {
      this.tagsControl.disable();
      this.tagsControl.setValue([]);
    } else {
      this.tagsControl.enable();
    }
  }

  canEditInspection(): boolean {
    return this.isEdit;
  }

  // Whether the contents of the dialog have been changed
  checkNoChange(): boolean {
    return this.titleControl.value == null
      && JSON.stringify(this.tagsControl.value) === JSON.stringify([])
      && this.markControl.value == null
      && this.commentControl.value === ''
      && this.isCommentClear === false
      && this.isTagsClear === false;
  }

  /** Number of selected images
   */
  getSelectedImageCount(): number {
    return this.data.selectedImages?.filter(img => this.isImageFileType(img.mimeType)).length;
  }

  /** Number of selected videos
   */
  getSelectedVideoCount(): number {
    return this.data.selectedImages?.filter(img => !this.isImageFileType(img.mimeType)).length;
  }

  isImageFileType(mimetype: string): boolean {
    return mimetype === IMAGE_FILETYPE.JPEG || mimetype === IMAGE_FILETYPE.PNG || mimetype === IMAGE_FILETYPE.BMP
      || mimetype === IMAGE_FILETYPE.TIF || mimetype === IMAGE_FILETYPE.TIFF;
  }

  /** Update the selected images
   */
  imagePropertyMultipleUpdate() {
    this.isProcessing = true;
    if (this.isCommentClear || this.commentControl.value !== '') {
      this.isNoChangeComment = false;
    }
    if (!this.isTagsClear && this.tagsControl.value.length === 0) {
      this.tagsControl.setValue(null);
    }

    const inspMultiUpdate = new InspDataMultiUpdate(this.markControl.value, this.titleControl.value,
      this.tagsControl.value, this.commentControl.value, this.isNoChangeComment);
    this.updateEvent.emit(inspMultiUpdate);
    this.isProcessing = false;
  }

  /** Open delete confirmation dialog
   */
  openMultipleDeleteDialog() {
    this.deleteEvent.emit();
  }

  /** Open save confirmation dialog
   */
  close() {
    if (this.checkNoChange()) {
      this.dialogRef.close();
    } else {
      const dialogRef = this.dialog.open(CommonSaveConfirmDialogComponent, {
        width: '450px',
        height: '280px',
        disableClose: true,
        data: { isMove: false, isError: false }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.imagePropertyMultipleUpdate();
        } else if (result !== '') {
          this.dialogRef.close();
        }
      });
    }
  }

  protected readonly MARK_DISABLED_COLOR = MARK_DISABLED_COLOR;
}
